/**
 * CalendarGrid Component
 *
 * Monthly calendar grid with day selection.
 */

import React, { useState, useMemo } from 'react';
import { CalendarDay as CalendarDayData } from '../hooks';
import { CalendarDay } from './CalendarDay';

interface CalendarGridProps {
  month: Date;
  days: CalendarDayData[];
  isLoading: boolean;
  selectedDates: Date[];
  onDateSelect: (dates: Date[]) => void;
  onDayClick?: (date: Date, data?: CalendarDayData) => void;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const isSameDay = (date1: Date, date2: Date): boolean => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

const isBefore = (date1: Date, date2: Date): boolean => {
  const d1 = new Date(date1.getFullYear(), date1.getMonth(), date1.getDate());
  const d2 = new Date(date2.getFullYear(), date2.getMonth(), date2.getDate());
  return d1.getTime() < d2.getTime();
};

const isToday = (date: Date): boolean => {
  const today = new Date();
  return isSameDay(date, today);
};

export const CalendarGrid: React.FC<CalendarGridProps> = ({
  month,
  days: calendarData,
  isLoading,
  selectedDates,
  onDateSelect,
  onDayClick,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<Date | null>(null);

  // Generate weeks for the month
  const weeks = useMemo(() => {
    const firstDay = new Date(month.getFullYear(), month.getMonth(), 1);
    const lastDay = new Date(month.getFullYear(), month.getMonth() + 1, 0);

    const startPadding = firstDay.getDay();
    const totalDays = lastDay.getDate();

    const days: (Date | null)[] = [];

    // Add padding for days before the first of the month
    for (let i = 0; i < startPadding; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let i = 1; i <= totalDays; i++) {
      days.push(new Date(month.getFullYear(), month.getMonth(), i));
    }

    // Split into weeks
    const weeks: (Date | null)[][] = [];
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7));
    }

    // Pad the last week if needed
    const lastWeek = weeks[weeks.length - 1];
    while (lastWeek.length < 7) {
      lastWeek.push(null);
    }

    return weeks;
  }, [month]);

  const handleDayMouseDown = (date: Date) => {
    setIsDragging(true);
    setDragStart(date);
    onDateSelect([date]);
  };

  const handleDayMouseEnter = (date: Date) => {
    if (isDragging && dragStart) {
      // Create range between dragStart and current date
      const start = dragStart < date ? dragStart : date;
      const end = dragStart < date ? date : dragStart;

      const range: Date[] = [];
      const current = new Date(start);
      while (current <= end) {
        range.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }

      onDateSelect(range);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setDragStart(null);
  };

  const handleDayClick = (date: Date) => {
    const dayData = calendarData?.find((d) => isSameDay(d.date, date));
    onDayClick?.(date, dayData);
  };

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="grid grid-cols-7 gap-1 mb-2">
          {WEEKDAYS.map((day) => (
            <div key={day} className="h-8 bg-gray-100 rounded" />
          ))}
        </div>
        {[...Array(5)].map((_, weekIdx) => (
          <div key={weekIdx} className="grid grid-cols-7 gap-1 mb-1">
            {[...Array(7)].map((_, dayIdx) => (
              <div key={dayIdx} className="h-24 bg-gray-100 rounded" />
            ))}
          </div>
        ))}
      </div>
    );
  }

  const today = new Date();

  return (
    <div
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      className="select-none"
    >
      {/* Weekday Headers */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {WEEKDAYS.map((day, idx) => (
          <div
            key={day}
            className={`text-center text-xs md:text-sm font-medium py-2 ${
              idx === 0 || idx === 6 ? 'text-gray-400' : 'text-gray-600'
            }`}
          >
            <span className="hidden md:inline">{day}</span>
            <span className="md:hidden">{day.charAt(0)}</span>
          </div>
        ))}
      </div>

      {/* Calendar Days */}
      {weeks.map((week, weekIdx) => (
        <div key={weekIdx} className="grid grid-cols-7 gap-1 mb-1">
          {week.map((date, dayIdx) => {
            if (!date) {
              return <div key={`empty-${dayIdx}`} className="min-h-[80px] md:min-h-[100px]" />;
            }

            const dayData = calendarData?.find((d) => isSameDay(d.date, date));
            const isSelected = selectedDates.some((d) => isSameDay(d, date));
            const isPast = isBefore(date, today) && !isToday(date);

            return (
              <CalendarDay
                key={date.toISOString()}
                date={date}
                data={dayData}
                isSelected={isSelected}
                isPast={isPast}
                isToday={isToday(date)}
                onMouseDown={() => !isPast && handleDayMouseDown(date)}
                onMouseEnter={() => !isPast && handleDayMouseEnter(date)}
                onClick={() => handleDayClick(date)}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default CalendarGrid;
